import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateRestoSlug } from "../utils/RestoSlugUtils";
import LoadingIndicator from "./LoadingIndicator";
import { buildApiUrl } from "../utils/ApiConfig";
import { CloseIcon } from "./Icon";
import { sidebarStateManager } from "../utils/SidebarStateManager";
import { useSidebarState } from "../hooks/useSidebarState";
import AnalyticsManager from "../utils/AnalyticsManager";
import styles from "./RestaurantDetailsSidebar.module.css";
import axios from "axios";

interface CityStats {
  name: string;
  restaurantCount: number;
  avgScore: number;
  reportCount: number;
  topRestaurants: { id: number; name: string; inspectionForks: number; address: string }[];
  worstRestaurants: { id: number; name: string; inspectionForks: number; address: string }[];
}

interface CityStatsContentProps {
  citySlug?: string;
  initialData?: CityStats;
}

const getScoreEmoji = (score: number) => {
  if (score === 5) return "😍";
  if (score === 4) return "😃";
  if (score === 3) return "🙂";
  if (score === 2) return "🤮";
  if (score === 1) return "💀";
  return "❓";
};

const getScoreLabel = (score: number, t: (key: string, fallback: string) => string) => {
  if (score >= 4) return t('score.excellent', 'Excellent');
  if (score === 3) return t('score.good', 'Good');
  if (score === 2) return t('score.poor', 'Poor');
  if (score === 1) return t('score.bad', 'Very Poor');
  return t('score.unknown', 'Unknown');
};

const getScoreColor = (score: number) => {
  if (score >= 4) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700/50';
  if (score === 3) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700/50';
  return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700/50';
};

function titleCase(str: string): string {
  return str.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const CityStatsContent: React.FC<CityStatsContentProps> = ({ citySlug, initialData }) => {
  const { t, i18n } = useTranslation();
  const { closeSidebar } = useSidebarState(sidebarStateManager);
  const [stats, setStats] = useState<CityStats | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [alertStatus, setAlertStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const lang = i18n.language || 'en';

  useEffect(() => {
    if (initialData || !citySlug) return;
    const fetchStats = async () => {
      setLoading(true);
      try {
        const url = buildApiUrl(`api/city/${encodeURIComponent(citySlug)}/stats`);
        const response = await axios.get(url, { timeout: 10000 });
        setStats(response.data);
      } catch {
        // Failed to fetch
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [citySlug, initialData]);

  // Set dynamic page title when stats are available
  useEffect(() => {
    if (!stats) return;
    const name = titleCase(stats.name);
    const isNL = lang === 'nl';
    const isFR = lang === 'fr';
    document.title = isNL
      ? `Restaurant Hygiëne in ${name} - Voedselveiligheid | Kauwen of Spauwen`
      : isFR
        ? `Hygiène des Restaurants à ${name} - Sécurité Alimentaire | Kauwen of Spauwen`
        : `Restaurant Food Safety in ${name} - Hygiene Scores | Kauwen of Spauwen`;
    // Dynamic meta description
    const desc = isNL
      ? `${stats.restaurantCount} restaurants in ${name} - gem. hygiëne ${stats.avgScore}/5. Check voor je gaat eten.`
      : isFR
        ? `${stats.restaurantCount} restaurants à ${name} - hygiène moy. ${stats.avgScore}/5. Vérifiez avant de manger.`
        : `${stats.restaurantCount} restaurants in ${name} - avg hygiene ${stats.avgScore}/5. Check before you eat.`;
    document.querySelector('meta[name="description"]')?.setAttribute('content', desc);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', desc);
  }, [stats, lang]);

  const cityName = titleCase(stats?.name || citySlug || '');

  const getRestoUrl = (id: number, name: string) => {
    const slug = generateRestoSlug(name, id);
    return `/${lang}/restaurant/${slug}`;
  };

  const handleRestaurantClick = (e: React.MouseEvent, id: number, name: string) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('restaurantSelected', {
      detail: { restaurantId: id.toString(), openSidebar: true }
    }));
    window.history.pushState({}, '', getRestoUrl(id, name));
  };

  // Extract just the street from the full address
  const shortAddress = (addr: string) => {
    // Remove city name, postal code, and "Belgique"
    return addr.split(/\d{4}/)[0].trim();
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`flex-grow overflow-y-auto overflow-x-hidden ${styles.customScrollbar}`}>
        <div className="pl-4 pr-2 pt-4 pb-16">
          {/* Header */}
          <div className="flex justify-between items-center mb-1">
            <div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {cityName}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('city.subtitle', 'Restaurant hygiene overview')}
              </p>
            </div>
            <button
              onClick={closeSidebar}
              className="p-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <LoadingIndicator isLoading={true} />
            </div>
          )}

          {!loading && !stats && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-2">
                {t('city.noData', 'No data available for {{city}} yet', { city: cityName })}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 max-w-[250px]">
                {t('city.noDataHint', 'Try searching for restaurants in this area on the map')}
              </p>
            </div>
          )}

          {!loading && stats && (
            <>
              {/* Key stat - percentage passing */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-4 mt-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                      {stats.avgScore > 0 ? `${stats.avgScore}` : '—'}
                      <span className="text-lg text-gray-400 font-normal">/5</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t('city.avgExplain', 'avg hygiene score across {{count}} restaurants', { count: stats.restaurantCount })}
                    </p>
                  </div>
                  <div className="text-4xl">
                    {getScoreEmoji(Math.round(stats.avgScore))}
                  </div>
                </div>
              </div>

              {/* Area alerts signup */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700/30 mb-4">
                <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">
                  {t('city.alertTitle', 'Get alerts for {{city}}', { city: cityName })}
                </h4>
                <p className="text-xs text-blue-600/70 dark:text-blue-300/70 mb-3">
                  {t('city.alertDescription', 'Be the first to know when food safety issues appear in {{city}}', { city: cityName })}
                </p>
                {alertStatus === 'success' ? (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('city.alertSuccess', 'You\'re subscribed!')}
                  </div>
                ) : (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.target as HTMLFormElement;
                    const emailInput = form.elements.namedItem('email') as HTMLInputElement;
                    const email = emailInput.value;
                    if (!email) return;
                    setAlertStatus('loading');
                    try {
                      await axios.post(buildApiUrl('api/subscriptions/area'), { email, cityName: cityName.toLowerCase() });
                      setAlertStatus('success');
                      AnalyticsManager.getInstance().trackEvent('area_alert_subscribe', { city: cityName, event_category: 'retention' });
                    } catch {
                      setAlertStatus('error');
                    }
                  }} className="flex gap-2">
                    <input
                      name="email"
                      type="email"
                      placeholder={t('city.alertPlaceholder', 'your@email.com')}
                      required
                      className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                    <button
                      type="submit"
                      disabled={alertStatus === 'loading'}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {alertStatus === 'loading' ? '...' : t('city.alertButton', 'Alert me')}
                    </button>
                  </form>
                )}
                {alertStatus === 'error' && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{t('city.alertError', 'Already subscribed or try again later')}</p>
                )}
              </div>

              {/* Restaurants to avoid - DANGER FIRST */}
              {stats.worstRestaurants.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    {t('city.avoid', 'Avoid These')}
                  </h4>
                  <div className="space-y-1.5">
                    {stats.worstRestaurants.map((resto) => (
                      <a key={resto.id} href={getRestoUrl(resto.id, resto.name)} onClick={(e) => handleRestaurantClick(e, resto.id, resto.name)}
                        className="bg-red-50/50 dark:bg-red-900/10 rounded-lg p-3 border border-red-100 dark:border-red-900/30 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex justify-between items-center gap-2 no-underline">
                        <div className="min-w-0 flex-1">
                          <h5 className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{resto.name.split('|')[0].trim()}</h5>
                          <p className="text-xs text-gray-400 truncate">{shortAddress(resto.address)}</p>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getScoreColor(resto.inspectionForks)}`}>
                          {getScoreLabel(resto.inspectionForks, t)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Top rated */}
              {stats.topRestaurants.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('city.safe', 'Safe Choices')}
                  </h4>
                  <div className="space-y-1.5">
                    {stats.topRestaurants.map((resto) => (
                      <a key={resto.id} href={getRestoUrl(resto.id, resto.name)} onClick={(e) => handleRestaurantClick(e, resto.id, resto.name)}
                        className="bg-green-50/50 dark:bg-green-900/10 rounded-lg p-3 border border-green-100 dark:border-green-900/30 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors flex justify-between items-center gap-2 no-underline">
                        <div className="min-w-0 flex-1">
                          <h5 className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{resto.name.split('|')[0].trim()}</h5>
                          <p className="text-xs text-gray-400 truncate">{shortAddress(resto.address)}</p>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getScoreColor(resto.inspectionForks)}`}>
                          {getScoreLabel(resto.inspectionForks, t)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Data-driven prose for SEO uniqueness */}
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                {t('city.prose.overview', '{{city}} has {{count}} inspected restaurants with an average hygiene score of {{avg}}/5.', {
                  city: cityName,
                  count: stats.restaurantCount,
                  avg: stats.avgScore > 0 ? stats.avgScore.toFixed(1) : '—'
                })}
                {' '}
                {stats.reportCount > 0 && t('city.prose.reports', '{{count}} food safety reports have been filed by diners in {{city}}.', {
                  count: stats.reportCount,
                  city: cityName
                })}
                {stats.topRestaurants.length > 0 && (' ' + t('city.prose.topPick', 'Top rated: {{name}}.', {
                  name: stats.topRestaurants[0].name.split('|')[0].trim()
                }))}
                {stats.worstRestaurants.length > 0 && (' ' + t('city.prose.warning', '{{count}} restaurants flagged for poor hygiene.', {
                  count: stats.worstRestaurants.length
                }))}
              </p>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CityStatsContent;
